import "@tento-ui/components/round";
import "@tento-chat/term/session-replay";

import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Round } from "@tento-ui/components/round";
import { SessionReplay } from "@tento-chat/term/session-replay";

import type { PageNode } from "@tento-ui/brand/page-model";
import { setTextOrHide, stringProp } from "./node-props";
import { tshVars } from "./tokens";

const CAPTURE_TIMING: SessionReplay.Timing = { char: 0, work: 0, blank: 0 };

/**
 * The panel's corner radius, mirroring `--tsh-radius` in `tokens.ts`.
 *
 * It is a number rather than the token because a squircle corner is not a
 * border-radius: `Round` feeds the value to path arithmetic that produces an
 * SVG outline, so it needs a scalar, and `css` takes no interpolation values —
 * a `${}` inside one of these templates is silently dropped along with
 * everything after it. The two must be changed together; the token is what the
 * rest of the page reads.
 */
const PANEL_RADIUS = 10;

/**
 * How square the corner is. 0.6 is the smoothing every other panel in the
 * design system uses (`Round.Props.MODAL`, `FLAT_CARD`), so a terminal frame
 * sits in the same family as the cards around it rather than announcing itself
 * with a rounder corner.
 */
const PANEL_SMOOTHING = 0.6;

// A captured terminal figure. One node can show a single terminal, a pair, or
// a three-pane session. Multi-pane figures share one outer frame so the reader
// sees the panes as one interaction; on narrow screens they stack in source
// order without changing the content.
//
// The frame is a `std-round` rather than a `border-radius` box, and that is a
// real difference rather than a refactor. Two things come with it:
//
//   - the corner is the design system's squircle, drawn as an SVG outline, so
//     the terminal frame matches every card on the site instead of being the
//     one rounded rectangle that is rounded a different way;
//   - clipping is a `clip-path` on the panes, not `overflow: hidden` on an
//     ancestor. That matters because `overflow: hidden` makes an element a
//     scroll container even when nothing scrolls, and a scroll container over
//     a figure captures the reader's wheel — the exact failure this page shipped
//     once already, where the article stopped moving whenever the pointer
//     crossed a terminal. A clip path clips and does not scroll.
@component({ name: "tsh-session" })
export class TshSession extends LonaWebComponent {
  private $panel: Round = this.hydrate<Round>("panel");
  private $replay: SessionReplay = this.hydrate<SessionReplay>("replay");
  private $replayB: SessionReplay = this.hydrate<SessionReplay>("replay-b");
  private $replayC: SessionReplay = this.hydrate<SessionReplay>("replay-c");
  private $paneB: HTMLElement = this.$("pane-b");
  private $paneC: HTMLElement = this.$("pane-c");
  private $caption: HTMLElement = this.$("caption");
  private $label: HTMLElement = this.$("label");
  private $labelB: HTMLElement = this.$("label-b");
  private $labelC: HTMLElement = this.$("label-c");

  bind(node: PageNode): void {
    const sampleB: Option<string> = stringProp(node, "sampleB") ?? null;
    const sampleC: Option<string> = stringProp(node, "sampleC") ?? null;

    // The ground and the hairline are read from the terminal palette rather
    // than passed as literals: these resolve inside the squircle's own shadow
    // root, because a custom property inherits through one.
    this.$panel.bind({
      bg: "var(--tsh-term-bg)",
      border: "var(--tsh-term-edge)",
      borderWidth: 1,
      radius: PANEL_RADIUS,
      continuous: { smoothing: PANEL_SMOOTHING },
      clip: true,
    });

    setTextOrHide(this.$label, stringProp(node, "label"));
    this.$replay.bind({
      sample: stringProp(node, "sample") ?? "",
      $line: null,
      timing: CAPTURE_TIMING,
      prompt: stringProp(node, "prompt"),
    });

    this.$paneB.toggleAttribute("hidden", sampleB === null);
    setTextOrHide(this.$labelB, stringProp(node, "labelB"));
    if (sampleB !== null) {
      this.$replayB.bind({
        sample: sampleB,
        $line: null,
        timing: CAPTURE_TIMING,
        prompt: null,
      });
    }

    this.$paneC.toggleAttribute("hidden", sampleC === null);
    setTextOrHide(this.$labelC, stringProp(node, "labelC"));
    if (sampleC !== null) {
      this.$replayC.bind({
        sample: sampleC,
        $line: null,
        timing: CAPTURE_TIMING,
        prompt: null,
      });
    }

    this.toggleAttribute("file", stringProp(node, "file") !== null);
    this.toggleAttribute("multipane", sampleB !== null);
    this.toggleAttribute("triptych", sampleC !== null);
    setTextOrHide(this.$caption, stringProp(node, "caption"));
  }

  static $styles = [
    tshVars,
    css`
      :host {
        display: block;
        padding: 40px var(--tsh-gutter, 32px) 0;
        font-family: var(--tsh-mono);
      }

      figure {
        width: 100%;
        max-width: var(--tsh-panel, 680px);
        margin: 0;
      }

      :host([multipane]) figure {
        max-width: var(--tsh-wide-panel, 1100px);
      }

      /* Rules on the host of a shadow tree beat that tree's own :host rules, so
         these override Round's defaults (display: flex, width: 100%, and a
         0.3s transition on every property — which on a panel this size animates
         the whole frame the first time it is laid out). */
      #panel {
        display: block;
        width: fit-content;
        max-width: 100%;
        transition: none;
      }

      :host([multipane]) #panel {
        width: 100%;
      }

      /* The grid lives one level in, on the element Round clips. Round paints
         its ground and outline behind its slotted content, so the thing that
         gets the squircle clip has to be the thing with the panes in it. */
      :host([multipane]) #panes {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      :host([triptych]) #panes {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }

      .pane {
        min-width: 0;
      }

      .pane + .pane {
        border-left: 1px solid var(--tsh-term-edge);
      }

      .label {
        display: flex;
        align-items: baseline;
        gap: 0.7ch;
        padding: 10px 14px;
        border-bottom: 1px solid var(--tsh-term-edge);
        color: var(--tsh-term-dim);
        font-size: calc(0.5rem / 0.7);
      }

      .label::before {
        content: "●";
        color: var(--tsh-term-accent);
      }

      #pane-b .label::before {
        color: var(--tsh-term-node);
      }

      #pane-c .label::before {
        color: var(--tsh-term-added);
      }

      #replay,
      #replay-b,
      #replay-c {
        --session-replay-max-rows: 20;
        --session-replay-scrollbar-size: 8px;
        --session-replay-scrollbar-track: transparent;
        --session-replay-scrollbar-thumb: var(--tsh-term-dim);
        --session-replay-scrollbar-thumb-hover: var(--tsh-term-fg);
        --session-replay-focus-outline: 1px solid var(--tsh-term-dim);
        --session-replay-padding-inline: var(--tsh-term-gutter);
        --session-replay-padding-block: 12px;
        --session-replay-band-gap: 0.66lh;
        --session-replay-prompt-color: var(--tsh-term-dim);
        --session-replay-prompt-weight: 400;
        --session-replay-prompt-space: 10px;
        --session-replay-prompt-rule: 1px solid var(--tsh-term-edge);
        --session-replay-caret-width: 2px;
        display: block;
        width: calc(var(--tsh-term-cols) * 1ch + 2 * var(--tsh-term-gutter));
        max-width: 100%;
        color: var(--tsh-term-fg);
        font-size: calc(var(--small-size) / 0.7);
        /* Rounded to a whole pixel so a row is an integral cell height.
           Fractional leading (1.6 * 12.857px) stacks rows on fractional
           offsets, and the thread form positions each turn by translateY of
           the measured height — a fractional translate rasterizes glyphs
           differently than flow does, which is visible as a blur at every
           turn boundary. Whole-pixel rows make the two forms byte-identical,
           and cost the figure less than half a pixel per row. */
        line-height: round(nearest, 1.6em, 1px);
      }

      :host([multipane]) #replay,
      :host([multipane]) #replay-b,
      :host([multipane]) #replay-c {
        width: 100%;
      }

      :host([triptych]) #replay,
      :host([triptych]) #replay-b,
      :host([triptych]) #replay-c {
        font-size: calc(0.5rem / 0.7);
      }

      :host([file]) #replay,
      :host([file]) #replay-b,
      :host([file]) #replay-c {
        --session-replay-wrap: pre-wrap;
      }

      figcaption {
        display: block;
        max-width: var(--tsh-measure, 560px);
        margin: 12px 0 0;
        font-size: calc(0.5rem / 0.7);
        /* Rounded to a whole pixel so a row is an integral cell height.
           Fractional leading (1.6 * 12.857px) stacks rows on fractional
           offsets, and the thread form positions each turn by translateY of
           the measured height — a fractional translate rasterizes glyphs
           differently than flow does, which is visible as a blur at every
           turn boundary. Whole-pixel rows make the two forms byte-identical,
           and cost the figure less than half a pixel per row. */
        line-height: round(nearest, 1.6em, 1px);
        color: var(--tsh-faint, #9e9e9e);
      }

      @media (max-width: 760px) {
        :host([multipane]) #panes,
        :host([triptych]) #panes {
          grid-template-columns: minmax(0, 1fr);
        }

        .pane + .pane {
          border-top: 1px solid var(--tsh-term-edge);
          border-left: 0;
        }
      }

      [hidden] {
        display: none !important;
      }
    `,
  ];

  static $html = html`
    <figure>
      <std-round id="panel">
        <div id="panes">
          <div id="pane-a" class="pane">
            <div id="label" class="label"></div>
            <std-session-replay id="replay"></std-session-replay>
          </div>
          <div id="pane-b" class="pane" hidden>
            <div id="label-b" class="label"></div>
            <std-session-replay id="replay-b"></std-session-replay>
          </div>
          <div id="pane-c" class="pane" hidden>
            <div id="label-c" class="label"></div>
            <std-session-replay id="replay-c"></std-session-replay>
          </div>
        </div>
      </std-round>
      <figcaption id="caption"></figcaption>
    </figure>
  `;
}
